import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Show } from "@clerk/react";
import ClerkAuthProvider from "@/components/auth/ClerkAuthProvider";
import { MeProvider, useMe } from "@/context/MeContext";
import { Toaster } from "@/components/ui/sonner";
import { CookieBanner } from "@/components/CookieBanner";
import { VisitTracker } from "@/components/admin/Visitors";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import PendingAccess from "@/pages/PendingAccess";
import { MfaGate, SignedOutEverywhere, WasntMe } from "@/components/AccountSecurity";
import ApprovedSources from "@/pages/ApprovedSources";
import AdminAccess from "@/pages/admin/AdminAccess";
import Layout from "@/components/Layout";
import { SignInPage, SignUpPage } from "@/pages/AuthPages";
import Feed from "@/pages/Feed";
import CreateHub from "@/pages/CreateHub";
import CreatePost from "@/pages/CreatePost";
import Profile from "@/pages/Profile";
import Facts from "@/pages/Facts";
import FactDetail from "@/pages/FactDetail";
import Messages from "@/pages/Messages";
import Notifications from "@/pages/Notifications";
import Settings from "@/pages/Settings";
import SearchPage from "@/pages/SearchPage";
import AdminContributors from "@/pages/admin/AdminContributors";
import Preferences from "@/pages/Preferences";
import Welcome from "@/pages/Welcome";
import Live, { LiveDetail } from "@/pages/Live";
import SecurityStatement from "@/pages/SecurityStatement";
import SharePage from "@/pages/SharePage";
import PrivacyCentre from "@/pages/PrivacyCentre";
import { AgeRestricted } from "@/pages/AgeGate";
import AdminLive from "@/pages/admin/AdminLive";
import AdminDiscovery from "@/pages/admin/AdminDiscovery";
import AdminPrivacy from "@/pages/admin/AdminPrivacy";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminSources from "@/pages/admin/AdminSources";
import AdminSourceHealth from "@/pages/admin/AdminSourceHealth";
import AdminVerification from "@/pages/admin/AdminVerification";
import AdminModeration from "@/pages/admin/AdminModeration";
import AdminFactCheck from "@/pages/admin/AdminFactCheck";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import { Empty } from "@/components/common";
import Terms from "@/pages/Terms";
import HowItWorks from "@/pages/HowItWorks";
import AdminLogin from "@/pages/admin/AdminLogin";

const Gate = () => {
  const { access } = useMe();
  if (access === "mfa") return <MfaGate />;
  return access && access !== "approved" ? <PendingAccess status={access} /> : <Layout />;
};

const Protected = () => {
  return (
    <>
      <Show when="signed-in"><Gate /></Show>
      <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
    </>
  );
};

const AdminOnly = ({ children }) => {
  const { me } = useMe();
  return me?.role === "admin" ? children : <Empty testid="admin-denied" title="Administrators only" text="This area is restricted to Middle administrators." />;
};

export default function App() {
  return (
    <BrowserRouter>
      <ClerkAuthProvider>
        <MeProvider>
          <Routes>
            <Route path="/sign-in/*" element={<SignInPage />} />
            <Route path="/sign-up/*" element={<SignUpPage />} />
            <Route path="/share/:kind/:itemId" element={<SharePage />} />
            <Route path="/age-restricted" element={<AgeRestricted />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/security" element={<SecurityStatement />} />
            <Route path="/signed-out-everywhere" element={<SignedOutEverywhere />} />
            <Route path="/wasnt-me" element={<WasntMe />} />
            <Route path="/admin/login/*" element={<AdminLogin />} />
            <Route element={<Protected />}>
              <Route path="/" element={<Feed />} />
              <Route path="/create" element={<CreateHub />} />
              <Route path="/create/post" element={<CreatePost />} />
              <Route path="/profile" element={<Profile self />} />
              <Route path="/u/:userId" element={<Profile />} />
              <Route path="/friends" element={<Navigate to="/" replace />} />
              <Route path="/groups/*" element={<Navigate to="/" replace />} />
              <Route path="/welcome" element={<Welcome />} />
              <Route path="/preferences" element={<Preferences />} />
              <Route path="/privacy" element={<PrivacyCentre />} />
              <Route path="/live" element={<Live />} />
              <Route path="/live/:liveId" element={<LiveDetail />} />
              <Route path="/facts" element={<Facts />} />
              <Route path="/facts/sources" element={<ApprovedSources />} />
              <Route path="/facts/:factId" element={<FactDetail />} />
              <Route path="/messages" element={<Messages />} />
              <Route path="/messages/:convId" element={<Messages />} />
              <Route path="/notifications" element={<Notifications />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/search" element={<SearchPage />} />
              <Route path="/admin" element={<AdminOnly><AdminDashboard /></AdminOnly>} />
              <Route path="/admin/sources" element={<AdminOnly><AdminSources /></AdminOnly>} />
              <Route path="/admin/source-health" element={<AdminOnly><AdminSourceHealth /></AdminOnly>} />
              <Route path="/admin/verification" element={<AdminOnly><AdminVerification /></AdminOnly>} />
              <Route path="/admin/contributors" element={<AdminOnly><AdminContributors /></AdminOnly>} />
              <Route path="/admin/live" element={<AdminOnly><AdminLive /></AdminOnly>} />
              <Route path="/admin/discovery" element={<AdminOnly><AdminDiscovery /></AdminOnly>} />
              <Route path="/admin/privacy" element={<AdminOnly><AdminPrivacy /></AdminOnly>} />
              <Route path="/admin/moderation" element={<AdminOnly><AdminModeration /></AdminOnly>} />
              <Route path="/admin/fact-check" element={<AdminOnly><AdminFactCheck /></AdminOnly>} />
              <Route path="/admin/analytics" element={<AdminOnly><AdminAnalytics /></AdminOnly>} />
              <Route path="/admin/access" element={<AdminOnly><AdminAccess /></AdminOnly>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
          <CookieBanner />
          <VisitTracker />
          <Toaster position="top-center" style={{ pointerEvents: "auto" }} />
        </MeProvider>
      </ClerkAuthProvider>
    </BrowserRouter>
  );
}
